/**
 * Changes to the events here should be represented on the Help Center page:
 * https://support.mobi2go.com/hc/en-us/articles/360018842292-Google-Tag-Manager
 */

/**-----------------------------------------------------------------------------
 * Load in production only, or if forced
 */
import _ from 'lodash';

const load_analytics = /(\?|&)load_analytics/.test(document.location.search);
if (Mobi2Go.app.base_url === 'https://www.mobi2go.com' || load_analytics) {
    Mobi2Go.onReady(load);
}

function load() {
    const { Order } = Mobi2Go;
    const { Analytics } = Mobi2Go;
    const E = Analytics.EVENTS;

    /**-------------------------------------------------------------------------
     * Script Setup
     */
    const gtm_id = Mobi2Go.headoffice.analytics_google_tag_manager_id;

    if (!gtm_id) {
        return;
    }

    // Load GTM
    (function(w, d, s, l, i) {
        w[l] = w[l] || [];
        w[l].push({
            'gtm.start': new Date().getTime(),
            event: 'gtm.js',
        });
        const f = d.getElementsByTagName(s)[0];
        const j = d.createElement(s);
        const dl = l !== 'dataLayer' ? `&l=${l}` : '';
        j.async = true;
        j.src = `https://www.googletagmanager.com/gtm.js?id=${i}${dl}`;
        f.parentNode.insertBefore(j, f);
    })(window, document, 'script', 'dataLayer', gtm_id);

    /**-------------------------------------------------------------------------
     * Abstraction
     */

    function startSession() {
        const platform = Mobi2Go.app.getPlatform();
        let storefrontEmbedPath =
            document.location.host + document.location.pathname;

        window.dataLayer.appName = platform;
        window.dataLayer.appId = `${Mobi2Go.headoffice.domain_name}-${platform}`;
        window.dataLayer.currencyCode = Mobi2Go.headoffice.currency_abrv;

        // Apps always use a host + pathname similar to
        // https://www.mobi2go.com/store/page/3418/mobile.
        // We need a mechanism to allow us to override
        // this in the case that analytics are captured based on
        // the client's actual storefront embed path.
        if (Mobi2Go.headoffice.storefront_embed_path) {
            storefrontEmbedPath = Mobi2Go.headoffice.storefront_embed_path;
        }

        if (!window.m2g_ga_loaded_by_mobi2go) {
            window.dataLayer.push({
                event: 'PageView',
                url: document.location.href,
                page: {
                    storefrontEmbedPath,
                },
            });
        }
    }

    function loadLastOrder(lastOrderState) {
        _event(
            'order',
            'load last order',
            'load last order',
            lastOrderState.id
        );
    }

    function identifyLocation(location) {
        if (location) {
            _event('location', 'set location', 'set location', location.name);
        }
    }

    function identifyUser(customer) {
        if (customer) {
            window.dataLayer.userId = customer.id;
        }
    }

    function impressCategory(category) {
        _impressProducts(category.getProducts().toArray(), category.name);
        _event('menu', 'view category', 'view category', category.name);
    }

    function impressMenu(menu) {
        _impressProducts(menu.getCategoryProducts().toArray(), menu.name);
        _event('menu', 'view menu', 'view menu', menu.name);
    }

    function detailProduct(product) {
        const ecommerce = {
            detail: {
                products: [_formatProduct(product)],
            },
        };

        _event('detailProduct', null, null, null, ecommerce);
    }

    function addProductToOrder(product) {
        const ecommerce = {
            currencyCode: Mobi2Go.headoffice.currency_abrv,
            add: {
                products: [_formatProduct(product)],
            },
        };

        _event('AddToCart', null, null, null, ecommerce);
    }

    function removeProductFromOrder(product) {
        if (Order.isResetting()) {
            return;
        }

        const ecommerce = {
            remove: {
                products: [_formatProduct(product)],
            },
        };

        _event('removeFromCart', null, null, null, ecommerce);
    }

    function checkoutPaymentStep(order) {
        const ecommerce = {
            checkout: {
                actionField: {
                    step: 2,
                    option: order.getPaymentMethod(),
                },
                products: _formatProducts(),
            },
        };

        _event('checkoutPaymentStep2', null, null, null, ecommerce);
        _event('order', 'payment', 'payment');
    }

    function checkout() {
        const ecommerce = {
            checkout: {
                actionField: {
                    step: 1,
                },
                products: _formatProducts(),
            },
        };

        _event('checkoutPaymentStep1', null, null, null, ecommerce);
        _event('order', 'checkout', 'checkout');
    }

    function purchase(order) {
        window.dataLayer.orderId = order.getId();
        const ecommerce = {
            purchase: {
                actionField: {
                    id: order.getId(),
                    revenue: order
                        .getTotal()
                        .getTaxExclusivePrice()
                        .toFloat(),
                    tax: order
                        .getTotal()
                        .getTaxPrice()
                        .toFloat(),
                    shipping: order.getDeliveryFee().toFloat(),
                    coupon: order.getVoucherCode(),
                },
                products: _formatProducts(),
            },
        };

        _event('purchase', null, null, null, ecommerce);
        _event('order', 'confirmed', 'confirmed');
    }

    function startPayment(order) {
        _event('order', 'start payment', 'start payment', order.getId());
    }

    function saveCreditCard() {
        _event('order', 'save credit card', 'save credit card');
    }

    function completeRegistration() {
        _event('customer', 'complete registration', 'complete registration');
    }

    function confirmedOrder(order) {
        if (order.getPaymentMethod() === 'offline') {
            checkoutPaymentStep(order);
        }

        purchase(order);
    }

    function _event(event, action, label, value, ecommerce) {
        // Only raise events if we're not already doing so via m2g_ga
        if (!window.m2g_ga_loaded_by_mobi2go) {
            window.dataLayer.push({
                event,
                action,
                label,
                value,
                ecommerce,
            });
        }
    }

    function _impressProducts(menu_products, list_name) {
        const impressions = [];

        _.each(menu_products, function(menu_product, position) {
            const impression = _impressProduct(
                menu_product,
                list_name,
                position + 1
            );
            impressions.push(impression);
        });

        const ecommerce = {
            currencyCode: Mobi2Go.headoffice.currency_abrv,
            impressions,
        };

        _event('impressProducts', null, null, null, ecommerce);
    }

    function _impressProduct(menu_product, list_name, position) {
        const impression = {
            id: menu_product.id,
            name: menu_product.name,
            list: list_name,
            category: menu_product.category ? menu_product.category.name : null,
            position,
        };

        return impression;
    }

    function _formatProducts() {
        const products = [];

        _.each(Order.getProducts(), function(product) {
            const product_to_track = _formatProduct(product);
            products.push(product_to_track);
        });

        return products;
    }

    function _formatProduct(product) {
        var product = {
            id: product.menu_product.id,
            name: product.menu_product.name,
            price: product.price.toFloat(),
            quantity: product.quantity,
            variant: product.size ? product.size.menu_modifier.name : null,
            category: product.menu_product.category
                ? product.menu_product.category.name
                : null,
            position: product.menu_product.category_list_position
                ? product.menu_product.category_list_position
                : null,
        };

        return product;
    }

    /**-------------------------------------------------------------------------
     * Application Glue
     */

    startSession();

    Analytics.bind({
        LAST_ORDER_LOADED: loadLastOrder,
        IDENTIFY_LOCATION: identifyLocation,
        IDENTIFY_CUSTOMER: identifyUser,
        VIEWED_PRODUCT_MENU: impressMenu,
        VIEWED_PRODUCT_CATEGORY: impressCategory,
        ADDED_PRODUCT: addProductToOrder,
        EDITED_PRODUCT: detailProduct,
        REMOVED_PRODUCT: removeProductFromOrder,
        STARTED_PAYMENT: startPayment,
        CREDIT_CARD_SAVED: saveCreditCard,
        CONFIRMED_ORDER: confirmedOrder,
        STARTED_CHECKOUT: checkout,
        REGISTERED_CUSTOMER: completeRegistration,
    });
}
